import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DEFAULT_WISHLIST_FILTER,
  DISC_FORMATS,
  GENRES,
  TRASH_RETENTION_DAYS,
  activeWishlistFilterCount,
  daysLeftInTrash,
  filterAndSortWishlist,
  genreName,
  posterUrl,
  wishlistDisplayTitle,
  type BuyableState,
  type DiscFormat,
  type MessageKey,
  type SortDirection,
  type WishlistFilter,
  type WishlistItem,
  WISHLIST_SORT_KEYS,
  type WishlistSortKey,
} from '@physical-movies/core';
import { useCollection, useI18n, useToast } from '../app/context';
import { FormatChoice } from '../components/FormatChoice';
import { Icon } from '../components/Icon';
import { PageHeader } from '../components/Page';
import { FormatBadge } from '../components/Poster';
import { Sheet } from '../components/Sheet';
import { Button, Chip, EmptyState, IconButton, SearchInput, Segmented } from '../components/ui';
import { readStoredWishlistFilter, writeStoredWishlistFilter } from '../lib/filterStorage';
import { useFormatLabels } from '../lib/labels';
import { useScrollRestoration } from '../lib/useScrollRestoration';
import styles from './wishlist.module.css';

/** Keyed by WishlistSortKey, so a new sort cannot ship without a label. */
const SORT_LABEL: Record<WishlistSortKey, MessageKey> = {
  title: 'wishlist.sort.title',
  added: 'wishlist.sort.added',
  releaseDate: 'wishlist.sort.releaseDate',
  runtime: 'wishlist.sort.runtime',
};

export function Wishlist() {
  const { t, locale } = useI18n();
  const collection = useCollection();
  const toast = useToast();
  const navigate = useNavigate();
  const formatLabels = useFormatLabels();

  const [localFilter, setLocalFilter] = useState<WishlistFilter>(readStoredWishlistFilter);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [showTrash, setShowTrash] = useState(false);
  const [buying, setBuying] = useState<WishlistItem | null>(null);
  const [confirming, setConfirming] = useState<{ item: WishlistItem; forever: boolean } | null>(
    null,
  );

  /**
   * As on the collection: the filters are a passing thought and stay in the
   * tab, the sort is a preference and lives in the settings document.
   */
  const filter = useMemo<WishlistFilter>(
    () => ({
      ...localFilter,
      sort: collection.settings.wishlistSort.key,
      direction: collection.settings.wishlistSort.direction,
    }),
    [localFilter, collection.settings.wishlistSort],
  );

  useEffect(() => {
    writeStoredWishlistFilter(filter);
  }, [filter]);

  useScrollRestoration(showTrash ? 'wishlist-trash' : 'wishlist');

  const items = showTrash ? collection.wishlistTrash : collection.wishlist;
  const visible = useMemo(
    () => filterAndSortWishlist(items, filter, locale),
    [items, filter, locale],
  );

  const availableGenres = useMemo(() => {
    const present = new Set<number>();
    for (const item of collection.wishlist) for (const id of item.genreIds) present.add(id);
    return [...present]
      .filter((id) => id in GENRES)
      .sort((a, b) => (genreName(a, locale) ?? '').localeCompare(genreName(b, locale) ?? ''));
  }, [collection.wishlist, locale]);

  const activeCount = activeWishlistFilterCount(filter);
  const patch = (next: Partial<WishlistFilter>) =>
    setLocalFilter((current) => ({ ...current, ...next }));

  const setSort = (key: WishlistSortKey) =>
    void collection.saveSettings({
      ...collection.settings,
      wishlistSort: { ...collection.settings.wishlistSort, key },
    });
  const setDirection = (direction: SortDirection) =>
    void collection.saveSettings({
      ...collection.settings,
      wishlistSort: { ...collection.settings.wishlistSort, direction },
    });

  const toggleFormat = (format: DiscFormat) =>
    patch({
      formats: filter.formats.includes(format)
        ? filter.formats.filter((entry) => entry !== format)
        : [...filter.formats, format],
    });

  const toggleGenre = (id: number) =>
    patch({
      genreIds: filter.genreIds.includes(id)
        ? filter.genreIds.filter((entry) => entry !== id)
        : [...filter.genreIds, id],
    });

  const setBuyable = async (item: WishlistItem, buyable: boolean) => {
    await collection.updateWishlistItem(item.id, { buyable });
    toast.show(buyable ? t('wishlist.markedBuyable') : t('wishlist.markedUpcoming'));
  };

  const confirmRemoval = async () => {
    if (!confirming) return;
    const { item, forever } = confirming;
    const title = wishlistDisplayTitle(item, locale);
    setConfirming(null);

    if (forever) {
      await collection.deleteWishlistItem(item.id);
      toast.show(t('wishlist.trash.deletedForever'));
    } else {
      await collection.trashWishlistItem(item.id);
      toast.show(t('wishlist.movedToTrash', { title }));
    }
  };

  const restore = async (item: WishlistItem) => {
    await collection.restoreWishlistItem(item.id);
    toast.show(t('wishlist.trash.restored', { title: wishlistDisplayTitle(item, locale) }));
  };

  const trashCount = collection.wishlistTrash.length;

  if (collection.wishlist.length === 0 && trashCount === 0) {
    return (
      <div className={styles.page}>
        <PageHeader title={t('wishlist.title')} />
        <EmptyState
          icon="bookmark"
          title={t('wishlist.empty.title')}
          body={t('wishlist.empty.body')}
          action={
            <Button variant="primary" onClick={() => void navigate('/add')}>
              {t('add.title')}
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title={showTrash ? t('wishlist.trash') : t('wishlist.title')}
        subtitle={t('wishlist.count', { count: items.length })}
        actions={
          !showTrash && (
            <>
              {trashCount > 0 && (
                <IconButton
                  name="trash"
                  label={t('wishlist.trash.open', { count: trashCount })}
                  onClick={() => setShowTrash(true)}
                />
              )}
              <IconButton
                name="sliders"
                label={
                  activeCount > 0
                    ? t('collection.filtersActive', { count: activeCount })
                    : t('wishlist.filters')
                }
                variant={activeCount > 0 ? 'secondary' : 'ghost'}
                onClick={() => setFiltersOpen(true)}
              />
            </>
          )
        }
      />

      {showTrash ? (
        <>
          <p className={styles.trashNotice}>
            {t('wishlist.trash.empty.body', { days: TRASH_RETENTION_DAYS })}
          </p>
          <div style={{ marginBottom: 'var(--s4)' }}>
            <Button onClick={() => setShowTrash(false)}>
              <Icon name="chevronLeft" size={16} />
              {t('wishlist.trash.back')}
            </Button>
          </div>
        </>
      ) : (
        <div className={styles.toolbar}>
          <SearchInput
            value={filter.query}
            onValueChange={(query) => patch({ query })}
            placeholder={t('add.searchPlaceholder')}
            label={t('wishlist.title')}
            clearLabel={t('common.clear')}
          />
          <div className={styles.chips}>
            <Chip
              selected={filter.buyable === 'buyable'}
              onClick={() => patch({ buyable: filter.buyable === 'buyable' ? 'all' : 'buyable' })}
            >
              {t('wishlist.buyable.buyable')}
            </Chip>
            <Chip
              selected={filter.buyable === 'upcoming'}
              onClick={() => patch({ buyable: filter.buyable === 'upcoming' ? 'all' : 'upcoming' })}
            >
              {t('wishlist.buyable.upcoming')}
            </Chip>
            {DISC_FORMATS.map((format) => (
              <Chip
                key={format}
                selected={filter.formats.includes(format)}
                onClick={() => toggleFormat(format)}
              >
                {formatLabels.long(format)}
              </Chip>
            ))}
          </div>
        </div>
      )}

      {!showTrash && (filter.query || activeCount > 0) && (
        <p className={styles.results}>{t('wishlist.results', { count: visible.length })}</p>
      )}

      {visible.length === 0 ? (
        showTrash ? (
          <EmptyState
            icon="trash"
            title={t('wishlist.trash.empty.title')}
            body={t('wishlist.trash.empty.body', { days: TRASH_RETENTION_DAYS })}
            action={<Button onClick={() => setShowTrash(false)}>{t('wishlist.trash.back')}</Button>}
          />
        ) : (
          <EmptyState
            icon="search"
            title={t('wishlist.noResults.title')}
            body={t('wishlist.noResults.body')}
            action={
              <Button onClick={() => setLocalFilter(DEFAULT_WISHLIST_FILTER)}>
                {t('collection.clearFilters')}
              </Button>
            }
          />
        )
      ) : (
        <ul className={styles.list}>
          {visible.map((item) => {
            const poster = posterUrl(item.posterPath, 'w154');
            const title = wishlistDisplayTitle(item, locale);
            return (
              <li key={item.id} className={`${styles.row} ${showTrash ? styles.trashRow : ''}`}>
                <span className={styles.poster}>
                  {poster && <img src={poster} alt="" loading="lazy" decoding="async" />}
                </span>
                <span className={styles.text}>
                  <span className={styles.title}>{title}</span>
                  <span className={styles.meta}>
                    {item.year ?? '—'}
                    {item.desiredFormat && <FormatBadge format={item.desiredFormat} />}
                    {!item.buyable && (
                      <span className={styles.upcoming}>{t('wishlist.upcomingBadge')}</span>
                    )}
                  </span>
                  {showTrash ? (
                    <span className={styles.daysLeft}>
                      {t('wishlist.trash.daysLeft', { count: daysLeftInTrash(item) })}
                    </span>
                  ) : (
                    item.note && <span className={styles.note}>{item.note}</span>
                  )}
                </span>
                <span className={styles.rowActions}>
                  {showTrash ? (
                    <>
                      <IconButton
                        name="refresh"
                        label={t('wishlist.trash.restore')}
                        onClick={() => void restore(item)}
                      />
                      <IconButton
                        name="trash"
                        label={t('wishlist.trash.deleteForever')}
                        variant="danger"
                        onClick={() => setConfirming({ item, forever: true })}
                      />
                    </>
                  ) : (
                    <>
                      {/*
                        Always the clock, never a tick: the tick next to it
                        already means "I bought this", and two checkmarks in a
                        row is a guess rather than a control. The state is
                        carried by emphasis instead — highlighted while the
                        film is still waiting to come out.
                      */}
                      <IconButton
                        name="clock"
                        label={
                          item.buyable ? t('wishlist.markUpcoming') : t('wishlist.markBuyable')
                        }
                        variant={item.buyable ? 'ghost' : 'secondary'}
                        onClick={() => void setBuyable(item, !item.buyable)}
                      />
                      <IconButton
                        name="check"
                        label={t('wishlist.bought')}
                        onClick={() => setBuying(item)}
                      />
                      <IconButton
                        name="trash"
                        label={t('wishlist.remove')}
                        onClick={() => setConfirming({ item, forever: false })}
                      />
                    </>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      <RemovalSheet
        pending={confirming}
        onClose={() => setConfirming(null)}
        onConfirm={confirmRemoval}
      />

      <BoughtSheet
        item={buying}
        onClose={() => setBuying(null)}
        onMoved={(movieId, title) => {
          setBuying(null);
          toast.show(t('wishlist.moved', { title }));
          void navigate(`/movie/${movieId}`);
        }}
      />

      <Sheet
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        title={t('wishlist.filters')}
        closeLabel={t('common.close')}
        footer={
          <>
            <Button
              onClick={() => setLocalFilter({ ...DEFAULT_WISHLIST_FILTER, query: filter.query })}
            >
              {t('common.reset')}
            </Button>
            <Button variant="primary" onClick={() => setFiltersOpen(false)}>
              {t('wishlist.results', { count: visible.length })}
            </Button>
          </>
        }
      >
        <div className={styles.filterGroup}>
          <span className={`eyebrow ${styles.filterGroupTitle}`}>{t('wishlist.buyable')}</span>
          <Segmented<BuyableState>
            label={t('wishlist.buyable')}
            value={filter.buyable}
            onChange={(buyable) => patch({ buyable })}
            options={[
              { value: 'all', label: t('wishlist.buyable.all') },
              { value: 'buyable', label: t('wishlist.buyable.buyable') },
              { value: 'upcoming', label: t('wishlist.buyable.upcoming') },
            ]}
          />
        </div>

        <div className={styles.filterGroup}>
          <span className={`eyebrow ${styles.filterGroupTitle}`}>
            {t('wishlist.desiredFormatFilter')}
          </span>
          <div className={styles.filterOptions}>
            {DISC_FORMATS.map((format) => (
              <Chip
                key={format}
                selected={filter.formats.includes(format)}
                onClick={() => toggleFormat(format)}
              >
                {formatLabels.long(format)}
              </Chip>
            ))}
          </div>
        </div>

        {availableGenres.length > 0 && (
          <div className={styles.filterGroup}>
            <span className={`eyebrow ${styles.filterGroupTitle}`}>{t('filter.genres')}</span>
            <div className={styles.filterOptions}>
              {availableGenres.map((id) => (
                <Chip
                  key={id}
                  selected={filter.genreIds.includes(id)}
                  onClick={() => toggleGenre(id)}
                >
                  {genreName(id, locale)}
                </Chip>
              ))}
            </div>
          </div>
        )}

        <div className={styles.filterGroup}>
          <span className={`eyebrow ${styles.filterGroupTitle}`}>{t('collection.sort')}</span>
          <div className={styles.sortRow}>
            <div className={styles.filterOptions}>
              {WISHLIST_SORT_KEYS.map((key) => (
                <Chip key={key} selected={filter.sort === key} onClick={() => setSort(key)}>
                  {t(SORT_LABEL[key])}
                </Chip>
              ))}
            </div>
            <Segmented<SortDirection>
              label={t('collection.sort')}
              value={filter.direction}
              onChange={setDirection}
              options={[
                { value: 'asc', label: t('sort.ascending') },
                { value: 'desc', label: t('sort.descending') },
              ]}
            />
          </div>
        </div>
      </Sheet>
    </div>
  );
}

/** Removing something always asks first — from the list and from the trash. */
function RemovalSheet({
  pending,
  onClose,
  onConfirm,
}: {
  pending: { item: WishlistItem; forever: boolean } | null;
  onClose(): void;
  onConfirm(): void | Promise<void>;
}) {
  const { t, locale } = useI18n();
  const [busy, setBusy] = useState(false);

  const title = pending ? wishlistDisplayTitle(pending.item, locale) : '';
  const forever = pending?.forever ?? false;

  return (
    <Sheet
      open={pending !== null}
      onClose={onClose}
      title={
        forever
          ? t('wishlist.trash.deleteForeverConfirm.title', { title })
          : t('wishlist.deleteConfirm.title', { title })
      }
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="danger"
            disabled={busy}
            onClick={() => {
              setBusy(true);
              void Promise.resolve(onConfirm()).finally(() => setBusy(false));
            }}
          >
            {forever ? t('wishlist.trash.deleteForever') : t('wishlist.deleteConfirm.action')}
          </Button>
        </>
      }
    >
      <div className={styles.confirmBody}>
        <p className={styles.confirmText}>
          {forever
            ? t('wishlist.trash.deleteForeverConfirm.body')
            : t('wishlist.deleteConfirm.body', { days: TRASH_RETENTION_DAYS })}
        </p>
      </div>
    </Sheet>
  );
}

/** "I bought it" — the wishlist entry leaves the list and lands on the shelf. */
function BoughtSheet({
  item,
  onClose,
  onMoved,
}: {
  item: WishlistItem | null;
  onClose(): void;
  onMoved(movieId: string, title: string): void;
}) {
  const { t, locale } = useI18n();
  const collection = useCollection();
  const [format, setFormat] = useState<DiscFormat>('hd');
  const [busy, setBusy] = useState(false);

  const confirm = async () => {
    if (!item) return;
    setBusy(true);
    try {
      const movieId = await collection.moveWishlistItemToCollection(item, format);
      onMoved(movieId, wishlistDisplayTitle(item, locale));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet
      open={item !== null}
      onClose={onClose}
      title={item ? wishlistDisplayTitle(item, locale) : ''}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" disabled={busy} onClick={() => void confirm()}>
            <Icon name="check" size={16} />
            {busy ? t('common.saving') : t('wishlist.bought')}
          </Button>
        </>
      }
    >
      <div className={styles.boughtBody}>
        <FormatChoice value={format} onChange={setFormat} label={t('add.chooseFormat')} />
      </div>
    </Sheet>
  );
}
